import { parseHtmlString } from "@/utils/helpers/html";
import { Heading, Paragraph } from "@/components/ui/Typography"
import { WebsitePageSection } from "@/types"
import EditableSection from "@/components/admin/Content/WebPages/EditableSection";
import InlineStrategicPillarEditor from "@/components/admin/Content/WebPages/InlineEditors/InlineStrategicPillarEditor";
import { useStrategicPillarHighlights } from "@/lib/api/hooks/strategic-pillar";
import { StrategicPillar } from "@/lib/api/types";

// Helper function to format date
const formatPublishedDate = (dateString: string | null): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

const StrategicPillars = ({ data }: { data: WebsitePageSection }) => {
  // Fetch live highlights
  const { data: highlightsData } = useStrategicPillarHighlights();
  const highlights = highlightsData?.data || [];

  return (
    <EditableSection
      sectionTag={data.sectionTag}
      contentType={data.contentType}
      data={data}
      customEditor={InlineStrategicPillarEditor}
    >
      <div className="flex flex-col gap-4">
        {/* Header */}
        <div className="flex flex-col gap-2">
          <Paragraph className="bg-gray-50 rounded-2xl px-3 py-1 !text-sm flex items-center justify-center text-forestGreen-100 w-fit">
            Strategic Pillars ({highlights.length}/3)
          </Paragraph>
          <div className="flex items-center gap-6">
            <Heading level={4}>{data.title ?? "Priorities of AFSA"}</Heading>
          </div>
          <Paragraph dangerouslySetInnerHTML={parseHtmlString(data.description ?? "Strategic pillars for the landing page")} />
        </div>

        {/* Live Preview */}
        {highlights.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
            {highlights.map((pillar: StrategicPillar, index: number) => {
              const defaultImage = "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760288/Image-11_sxxnhn.png";

              return (
                <div key={pillar.strategicPillarId} className="flex flex-col gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  {/* Position Badge */}
                  <div className="inline-flex items-center gap-2">
                    <span className="px-2 py-1 bg-[#681628] text-white text-xs font-medium rounded">
                      Pillar {index + 1}
                    </span>
                  </div>

                  {/* Cover Image */}
                  <div className="relative w-full h-32 bg-gray-200 rounded overflow-hidden">
                    <img
                      src={pillar.coverImage || defaultImage}
                      alt={pillar.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        if (e.currentTarget.src !== defaultImage) {
                          e.currentTarget.src = defaultImage;
                        }
                      }}
                    />
                  </div>

                  {/* Content */}
                  <div className="flex flex-col gap-2">
                    <h4 className="font-semibold text-sm text-gray-900 line-clamp-2">{pillar.title}</h4>
                    {(pillar.datePublished || pillar.createdByUsername) && (
                      <div className="flex flex-wrap gap-x-3 gap-y-1">
                        {pillar.datePublished && (
                          <p className="text-xs text-gray-500"><span className="font-medium">Published date:</span> {formatPublishedDate(pillar.datePublished)}</p>
                        )}
                        {pillar.createdByUsername && (
                          <p className="text-xs text-gray-500"><span className="font-medium">Published by:</span> {pillar.createdByUsername}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Tags */}
                  {pillar.articleTag && Array.isArray(pillar.articleTag) && pillar.articleTag.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {pillar.articleTag.slice(0, 2).map((tag: string, i: number) => (
                        <span key={i} className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-8 text-center bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-gray-500 text-sm">No strategic pillars selected. Click edit to select up to 3 pillars.</p>
          </div>
        )}
      </div>
    </EditableSection>
  )
}

export default StrategicPillars